import { ACR_RESOURCE_TYPE, formatResourceType } from '../pages/NRSettings/NRUsersAndAccess/rbac.constants';

export const RESOURCE_TYPE_DISPLAY_ORDER = {
  [ACR_RESOURCE_TYPE.ORGANIZATION]: 0,
  [ACR_RESOURCE_TYPE.ASSET_GROUP]: 1,
  [ACR_RESOURCE_TYPE.ASSET]: 2
};

function getResourceTypeDisplayOrder(resourceType) {
  return RESOURCE_TYPE_DISPLAY_ORDER[resourceType] ?? 99;
}

/** Organization-scoped grants first, then asset group, then asset. */
export function sortByResourceScope(items = []) {
  return [...items].sort((a, b) => {
    const typeOrder = getResourceTypeDisplayOrder(a.resourceType) - getResourceTypeDisplayOrder(b.resourceType);
    if (typeOrder !== 0) return typeOrder;
    return (a.roleName || '').localeCompare(b.roleName || '', undefined, { sensitivity: 'base' });
  });
}

/**
 * Maps ACR records to normalized grants. Role-based permissions come from acr.rolePermissions.
 * Direct-permission ACRs use acr.directPermissions.
 */
export function normalizeUserGrants(acrs = []) {
  return sortByResourceScope(
    acrs
      .map(acr => {
        if (acr?.roleId && acr?.roleName) {
          return {
            acrId: acr.id,
            roleId: acr.roleId,
            roleName: acr.roleName,
            resourceType: acr.resourceType,
            resourceId: acr.resourceId,
            resourceName: acr.resourceName,
            entityType: acr.entityType,
            entityName: acr.entityName,
            permissions: acr.rolePermissions ?? []
          };
        }

        const directPermissions = acr?.directPermissions ?? [];
        if (directPermissions.length > 0) {
          return {
            acrId: acr.id,
            roleId: null,
            roleName: acr.roleName || 'Direct permissions',
            resourceType: acr.resourceType,
            resourceId: acr.resourceId,
            resourceName: acr.resourceName,
            entityType: acr.entityType,
            entityName: acr.entityName,
            permissions: directPermissions
          };
        }

        return null;
      })
      .filter(Boolean)
  );
}

/** Compact tag summary for a single grant. */
export function buildGrantTagSummary(grant) {
  const roleName = grant?.roleName || '';
  const resourceLabel = grant?.resourceType === ACR_RESOURCE_TYPE.ORGANIZATION ? '' : grant?.resourceName || formatResourceType(grant?.resourceType);

  return {
    roleName,
    resourceLabel,
    resourceType: grant?.resourceType
  };
}

/** Unique grant tag summaries for display lists. */
export function buildDisplayTags(grants = []) {
  const seen = new Set();

  const tags = grants.reduce((acc, grant) => {
    const summary = buildGrantTagSummary(grant);
    const key = `${summary.roleName}|${summary.resourceLabel}|${summary.resourceType}`;

    if (!summary.roleName || seen.has(key)) return acc;

    seen.add(key);
    acc.push(summary);
    return acc;
  }, []);

  return sortByResourceScope(tags);
}
